import { useState } from "react";
import { useTranslation } from "react-i18next";
import { guestNameSchema, SELF_REGISTER_ALLOWED_PREFIXES } from "@wedding/shared";
import { useTurnstile } from "../../hooks/use-turnstile";
import { useQrRegister } from "../../hooks/use-qr-register";
import { useRegistrationWindow } from "../../hooks/use-registration-window";
import { PhoneNumberInput } from "../../components/phone";
import { OtpDigits } from "../../components/OtpDigits";
import { cn } from "../../lib/utils";
import { ArrowRight, Loader2, MessageCircle, UserPlus, Heart, UserCheck, Lock, Clock } from "lucide-react";
import { PrivacyNote } from "../../components/privacy-note";

/**
 * Auth sayfasının KOYU tasarımına uyumlu kayıt formu.
 *
 * Kayıt MANTIĞI paylaşılan `useQrRegister` hook'unda (join sayfasıyla aynı
 * durum makinesi + aynı endpoint). Bu bileşen yalnız koyu UI'ı sağlar ve OTP
 * grid'ini login ile paylaşır (`OtpDigits`).
 *
 * SMS bütçesi: Turnstile + istemci tarafı yabancı-numara kapısı gönder'i
 * kilitler; "zaten kayıtlı" sunucudan SMS'siz döner → login'e yönlendiririz
 * (yeni istek yok). Telefon parent'ta tutulur, modlar arası korunur.
 */
interface AuthRegisterFormProps {
  phoneE164: string;
  phoneValid: boolean;
  onPhoneChange: (e164: string, valid: boolean) => void;
  /** "Zaten kayıtlısın" → login moduna geç (telefon parent'ta zaten paylaşık). */
  onSwitchToLogin: () => void;
}

const ALLOWED_PREFIX_LABEL = SELF_REGISTER_ALLOWED_PREFIXES.join(", ");

export const BUTTON_CLASS =
  "w-full bg-primary hover:bg-primary/90 active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-[background-color,opacity,transform] shadow-lg shadow-primary/20 flex items-center justify-center gap-2";

export const INPUT_CLASS =
  "w-full rounded-lg bg-white/5 border border-primary/20 px-4 py-3.5 text-base text-white placeholder:text-slate-500 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-[border-color,box-shadow]";

const LINK_BTN_CLASS =
  "mt-3 text-sm text-slate-400 hover:text-primary transition-colors cursor-pointer";

/**
 * Terminal durum ekranı — pure/soft/minimal. Nested kutu yok; glass-panel
 * içinde nefes alır. Yumuşak parıltılı dairesel ikon + zarif serif başlık +
 * sade metin + tek net aksiyon. Alanlar `.auth-cascade` ile sırayla süzülür.
 */
function TerminalState({
  icon,
  title,
  hint,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  stateKey,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  stateKey: string;
}) {
  return (
    <div key={stateKey} className="auth-cascade flex flex-col items-center py-4 text-center">
      <span className="mb-5 grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <h3 className="font-serif text-[1.6rem] leading-tight text-white">{title}</h3>
      <p className="mt-2 max-w-[16rem] text-sm leading-relaxed text-slate-400">{hint}</p>
      <button type="button" onClick={onPrimary} className={cn("mt-7", BUTTON_CLASS)}>
        {primaryLabel}
        <ArrowRight className="h-4 w-4" />
      </button>
      {secondaryLabel && onSecondary && (
        <button type="button" onClick={onSecondary} className={LINK_BTN_CLASS}>
          {secondaryLabel}
        </button>
      )}
    </div>
  );
}

export function AuthRegisterForm({
  phoneE164,
  phoneValid,
  onPhoneChange,
  onSwitchToLogin,
}: AuthRegisterFormProps) {
  const { t } = useTranslation();
  const { open: windowOpen } = useRegistrationWindow();
  const { step, loading, error, register, verify, reset } = useQrRegister();
  const { containerRef, token: turnstileToken, error: turnstileError, reset: resetTurnstile } = useTurnstile();

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const code = digits.join("");

  const nameOk = guestNameSchema.safeParse(name).success;
  // Twilio Verify +90 geo-kilitli → başka prefix'e kod gönderilemiyor. Kullanıcı
  // gönder'e basıp 409 yemesin diye ülke seçer seçmez uyarıyoruz.
  const foreignNumber = phoneE164 !== "" && !SELF_REGISTER_ALLOWED_PREFIXES.some((p) => phoneE164.startsWith(p));
  const canSubmit = !loading && Boolean(turnstileToken) && nameOk && phoneValid && !foreignNumber;

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !turnstileToken) return;
    await register(guestNameSchema.parse(name), phoneE164, turnstileToken, smsConsent);
    resetTurnstile();
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    await verify(code);
  }

  /** Sunucu kodu/mesajı → kullanıcıya gösterilecek metin. */
  function errorText(serverError: string): string {
    if (!serverError) return "";
    if (serverError === "Banned") return t("auth.banned");
    if (serverError === "service_unavailable") return t("auth.otpUnavailable");
    if (serverError === "foreign_number") return t("join.foreignNumber");
    if (serverError.startsWith("Name")) return t("join.invalidName");
    return serverError;
  }

  // ── Terminal durumlar (pure/soft/minimal) ────────────────────────────
  if (step === "already_registered") {
    return (
      <TerminalState
        stateKey="already"
        icon={<UserCheck className="h-5 w-5" />}
        title={t("auth.alreadyRegisteredTitle")}
        hint={t("auth.alreadyRegisteredDesc")}
        primaryLabel={t("auth.alreadyRegisteredSwitch")}
        onPrimary={onSwitchToLogin}
        secondaryLabel={t("common.back")}
        onSecondary={reset}
      />
    );
  }

  if (step === "closed" || !windowOpen) {
    return (
      <TerminalState
        stateKey="closed"
        icon={<Lock className="h-5 w-5" />}
        title={t("join.registrationClosed")}
        hint={t("join.registrationClosedHint")}
        primaryLabel={t("join.goToLogin")}
        onPrimary={onSwitchToLogin}
      />
    );
  }

  if (step === "cap_reached") {
    return (
      <TerminalState
        stateKey="cap"
        icon={<Clock className="h-5 w-5" />}
        title={t("join.capReached")}
        hint={t("join.capReachedHint")}
        primaryLabel={t("join.goToLogin")}
        onPrimary={onSwitchToLogin}
      />
    );
  }

  // Misafir kotası dolu — listedeki misafirler yine giriş yapabilir.
  if (step === "guest_limit") {
    return (
      <TerminalState
        stateKey="guest-limit"
        icon={<Clock className="h-5 w-5" />}
        title={t("join.guestLimitReached")}
        hint={t("join.guestLimitReachedHint")}
        primaryLabel={t("join.goToLogin")}
        onPrimary={onSwitchToLogin}
      />
    );
  }

  // ── OTP adımı (login ile aynı grid) ──────────────────────────────────
  if (step === "otp") {
    return (
      <form key="otp" onSubmit={handleVerify} className="auth-cascade space-y-5" noValidate>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-serif text-white">{t("auth.verifyTitle")}</h2>
          </div>
          <p className="text-sm text-slate-400">{t("join.successOtp")}</p>
        </div>
        <OtpDigits digits={digits} onChange={setDigits} autoFocus />
        {error && (
          <p role="alert" className="text-sm text-red-400">{errorText(error)}</p>
        )}
        <button type="submit" disabled={loading || code.length < 6} className={BUTTON_CLASS}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
          {loading ? t("auth.verifying") : t("auth.verify")}
        </button>
        <button type="button" onClick={reset} className={LINK_BTN_CLASS}>
          {t("auth.changeNumber")}
        </button>
      </form>
    );
  }

  // ── Kayıt formu ──────────────────────────────────────────────────────
  return (
    <form key="form" onSubmit={handleRegister} className="auth-cascade space-y-5" noValidate>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-serif text-white">{t("auth.registerTitle")}</h2>
        </div>
        <p className="text-sm text-slate-400">{t("auth.registerDesc")}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="auth-register-name" className="block text-sm font-medium text-slate-300">
          {t("join.nameLabel")}
        </label>
        <input
          id="auth-register-name"
          type="text"
          autoComplete="name"
          placeholder={t("join.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
          required
          maxLength={80}
          aria-invalid={nameTouched && name !== "" && !nameOk}
          className={INPUT_CLASS}
        />
        {nameTouched && name !== "" && !nameOk && (
          <p role="alert" className="mt-1.5 text-xs text-red-400">{t("join.invalidName")}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="auth-register-phone" className="block text-sm font-medium text-slate-300">
          {t("auth.phoneLabel")}
        </label>
        <PhoneNumberInput
          id="auth-register-phone"
          variant="dark"
          valueE164={phoneE164}
          onChange={(e164, meta) => onPhoneChange(e164, meta.isValid)}
        />
        {foreignNumber && (
          <p role="alert" className="mt-2 text-xs leading-relaxed text-red-400">
            {t("join.foreignNumber")}{" "}
            <span className="text-slate-400">{t("join.foreignNumberHint", { prefixes: ALLOWED_PREFIX_LABEL })}</span>
          </p>
        )}
      </div>

      {/* KVKK: davet/hatırlatma SMS'i için açık rıza — varsayılan İŞARETSİZ */}
      <label className="flex cursor-pointer select-none items-start gap-2.5">
        <input
          type="checkbox"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-[#8d6b5e]"
        />
        <span className="text-xs leading-relaxed text-slate-400">{t("join.smsConsentLabel")}</span>
      </label>
      <PrivacyNote className="-mt-1 text-xs leading-relaxed text-slate-400" linkClassName="hover:text-slate-200" />

      <div ref={containerRef} className="flex justify-center" />

      {(error || turnstileError) && (
        <p role="alert" className="text-sm text-red-400">{errorText(error) || turnstileError}</p>
      )}

      <button type="submit" disabled={!canSubmit} className={BUTTON_CLASS}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {loading ? t("join.submitting") : t("join.submit")}
      </button>
    </form>
  );
}
